import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { XCloseIcon } from '@untitledui-icons/react/line'

import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/utils'
import {
  PREVIEW_FRAME_BODIES,
  previewSiteWorld,
  previewWebsiteHostname,
  resolvePreviewWebsiteUrl,
  type PreviewDevice,
} from './widgetConfig.preview'
import {
  contrastingFill,
  isDarkSurface,
  widgetIconFill,
  widgetPanelInk,
  WIDGET_BUTTON_PIXELS,
  type WidgetStyleFormState,
} from './widgetConfig.serializers'

const BRAND_NAME = 'Bonnie'

/** Venues listed in the location picker, in the order the widget shows them. */
const PREVIEW_VENUE_LIMIT = 3

/**
 * How far the launcher may sit from its corner before it leaves the blank mock.
 * Live-site preview uses real page pixels inside a scaled viewport instead.
 */
const DEVICE_LIMITS: Record<PreviewDevice, { maxX: number; maxY: number }> = {
  desktop: { maxX: 180, maxY: 120 },
  mobile: { maxX: 60, maxY: 300 },
}

/** Gap between the launcher and the card above it. */
const CARD_GAP = 8

export interface PreviewVenue {
  id: number
  name: string
  address: string | null
}

function toPixels(value: string): number {
  const parsed = Number.parseInt(value, 10)
  return Number.isFinite(parsed) ? Math.max(parsed, 0) : 0
}

function WhatsappGlyph({ size, color }: { size: number; color: string }) {
  return (
    <svg
      data-preview-whatsapp-icon
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      style={{ color, fill: color }}
    >
      <path d="M17.5 14.4c-.3-.2-1.8-.9-2.1-1-.3-.1-.5-.1-.7.1s-.7.9-.9 1.1c-.2.2-.4.2-.7.1a7.4 7.4 0 0 1-3.7-3.2c-.2-.3 0-.5.1-.7l.6-.8c.1-.2.1-.4 0-.6l-.9-2.1c-.2-.5-.5-.5-.7-.5h-.6c-.2 0-.6.1-.9.4-.3.3-1.1 1-1.1 2.5s1.1 2.9 1.2 3.1a11 11 0 0 0 4.4 3.9c1.4.5 2 .6 2.7.5.4-.1 1.4-.6 1.6-1.2.2-.6.2-1.1.1-1.2z" />
      <path d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2a8.2 8.2 0 0 1-4.2-1.1l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2z" />
    </svg>
  )
}

// ============================================================================
// Widget mock
//
// This is the customer's website, not the dashboard, so nothing inside the
// stage follows the dashboard theme. Ink is derived from the panel / CTA /
// launcher colours so a dark brand palette still reads, in either dashboard theme.
// ============================================================================

interface WidgetStageProps {
  style: WidgetStyleFormState
  device: PreviewDevice
  venues: PreviewVenue[]
  isMultiVenue: boolean
  /** Real page pixels, used when the mock is showing a customer's site. */
  useSitePlacement: boolean
}

function WidgetStage({ style, device, venues, isMultiVenue, useSitePlacement }: WidgetStageProps) {
  const { t } = useTranslation()
  const base = 'resources.widgets.preview'

  const limits = DEVICE_LIMITS[device]
  const diameter = WIDGET_BUTTON_PIXELS[style.size]
  const isLeft = style.position === 'bottom-left'
  const rawOffsetX = toPixels(style.offsetX)
  const rawOffsetY = toPixels(style.offsetY)

  const offsetX = useSitePlacement
    ? rawOffsetX
    : Math.min(device === 'mobile' ? Math.round((rawOffsetX * 2) / 3) : rawOffsetX, limits.maxX)
  const offsetY = useSitePlacement ? rawOffsetY : Math.min(rawOffsetY, limits.maxY)
  const horizontal = isLeft ? { left: offsetX } : { right: offsetX }

  /** With nothing assigned yet the picker still needs a row to show its shape. */
  const listedVenues = venues.length
    ? venues.slice(0, PREVIEW_VENUE_LIMIT)
    : [{ id: 0, name: t(`${base}.venue_placeholder`), address: null }]

  const ink = widgetPanelInk(style.panelColor)
  const actionLabel = contrastingFill(style.actionColor)
  const iconFill = widgetIconFill(style.iconColor, style.buttonColor)
  const rowWash = isDarkSurface(style.panelColor)
    ? 'rgba(255,255,255,0.08)'
    : 'rgba(0,0,0,0.045)'

  return (
    <>
      {(isMultiVenue || !style.iconOnly) && (
        <div
          className="absolute w-[196px]"
          style={{ bottom: offsetY + diameter + CARD_GAP, ...horizontal }}
        >
          <span className="absolute top-0 right-0 z-1 flex size-[22px] items-center justify-center rounded-sm border border-gray-200 bg-white shadow-xs">
            <XCloseIcon className="size-[9px] text-gray-500" />
          </span>

          <div
            className="mt-[26px] min-h-[134px] rounded-xl border border-black/[0.07] p-3 shadow-[0_6px_18px_rgba(10,13,18,0.1)]"
            style={{ background: style.panelColor, color: ink.primary }}
          >
            {isMultiVenue ? (
              <>
                <p className="text-[11px] leading-[15px] font-semibold" style={{ color: ink.primary }}>
                  {t(`${base}.picker_title`)}
                </p>
                <p className="mt-[3px] text-[9px] leading-[13px]" style={{ color: ink.secondary }}>
                  {t(`${base}.picker_subtitle`)}
                </p>
                <div className="mt-2 flex flex-col gap-1">
                  {listedVenues.map((venue) => (
                    <span
                      key={venue.id}
                      className="flex flex-col rounded-sm px-[7px] py-[5px]"
                      style={{ background: rowWash }}
                    >
                      <span className="text-[8.5px] leading-3 font-semibold" style={{ color: ink.primary }}>
                        {venue.name}
                      </span>
                      {venue.address && (
                        <span className="truncate text-[7px] leading-[10px]" style={{ color: ink.muted }}>
                          {venue.address}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              </>
            ) : (
              <>
                <p className="text-[11px] leading-[15px] font-semibold" style={{ color: ink.primary }}>
                  {t(`${base}.card_title`)}
                </p>
                <p className="mt-[3px] text-[9px] leading-[13px]" style={{ color: ink.secondary }}>
                  {t(`${base}.card_subtitle`)}
                </p>
                <div
                  className="mt-[9px] flex h-5 items-center justify-center rounded-sm text-[8px] font-semibold"
                  style={{ background: style.actionColor, color: actionLabel }}
                >
                  {t(`${base}.card_cta`)}
                </div>
                <p className="mt-1.5 text-center text-[7px] leading-[10px]" style={{ color: ink.muted }}>
                  {t(`${base}.card_footer_prefix`)}{' '}
                  <span className="font-semibold" style={{ color: ink.primary }}>
                    {BRAND_NAME}
                  </span>
                </p>
              </>
            )}
          </div>
        </div>
      )}

      <span
        className="absolute flex items-center justify-center border border-white/35 shadow-[0_2px_8px_rgba(10,13,18,0.16)]"
        style={{
          width: diameter,
          height: diameter,
          borderRadius: style.shape === 'circle' ? '9999px' : `${Math.round(diameter * 0.26)}px`,
          background: style.buttonColor,
          color: iconFill,
          bottom: offsetY,
          ...horizontal,
        }}
      >
        <WhatsappGlyph size={Math.round(diameter * 0.5)} color={iconFill} />
      </span>
    </>
  )
}

// ============================================================================
// Live-site stage
//
// The iframe lays out at a real desktop or phone width, then the whole world
// — site plus launcher — is scaled down to the mock. Offsets stay in page
// pixels, so the button lands where it would on the customer's site.
// ============================================================================

interface ScaledWebsiteStageProps {
  device: PreviewDevice
  src: string
  title: string
  children: React.ReactNode
}

function ScaledWebsiteStage({ device, src, title, children }: ScaledWebsiteStageProps) {
  const frame = PREVIEW_FRAME_BODIES[device]
  const world = previewSiteWorld(device)

  return (
    <div className="relative overflow-hidden bg-white" style={{ width: frame.width, height: frame.height }}>
      <div
        data-preview-world
        className="absolute top-0 left-0 origin-top-left"
        style={{
          width: world.width,
          height: world.height,
          transform: `scale(${world.scale})`,
        }}
      >
        <iframe
          title={title}
          src={src}
          width={world.width}
          height={world.height}
          sandbox="allow-scripts allow-same-origin allow-forms"
          referrerPolicy="no-referrer"
          className="block border-0 bg-white"
          style={{ width: world.width, height: world.height }}
        />
        <div className="pointer-events-none absolute inset-0 z-1">{children}</div>
      </div>
    </div>
  )
}

// ============================================================================
// Preview column
// ============================================================================

interface WidgetPreviewProps {
  style: WidgetStyleFormState
  venues: PreviewVenue[]
  isMultiVenue: boolean
}

export function WidgetPreview({ style, venues, isMultiVenue }: WidgetPreviewProps) {
  const { t } = useTranslation()
  const base = 'resources.widgets.preview'

  const [device, setDevice] = useState<PreviewDevice>('desktop')
  // Session-only: shared templates must not store a preview site.
  const [websiteUrl, setWebsiteUrl] = useState('')
  const previewSrc = useMemo(() => resolvePreviewWebsiteUrl(websiteUrl), [websiteUrl])
  const chromeLabel = previewSrc
    ? previewWebsiteHostname(previewSrc)
    : t(`${base}.url_placeholder`)

  const devices: PreviewDevice[] = ['desktop', 'mobile']
  const stage = (
    <WidgetStage
      style={style}
      device={device}
      venues={isMultiVenue ? venues : []}
      isMultiVenue={isMultiVenue}
      useSitePlacement={Boolean(previewSrc)}
    />
  )
  const websiteStage = previewSrc ? (
    <ScaledWebsiteStage device={device} src={previewSrc} title={t(`${base}.website_title`)}>
      {stage}
    </ScaledWebsiteStage>
  ) : null

  return (
    <div className="flex flex-col items-start">
      <h2 className="mb-[21px] text-lg leading-7 font-semibold text-gray-900 dark:text-gray-100">
        {t(`${base}.title`)}
      </h2>

      <div className="mb-4 w-full max-w-[480px]">
        <Input
          label={t(`${base}.url_label`)}
          description={t(`${base}.url_unsupported`)}
          placeholder={t(`${base}.url_placeholder`)}
          value={websiteUrl}
          onChange={setWebsiteUrl}
        />
      </div>

      <div role="group" aria-label={t(`${base}.device_label`)} className="mb-4 flex gap-1">
        {devices.map((option) => (
          <button
            key={option}
            type="button"
            aria-pressed={device === option}
            onClick={() => setDevice(option)}
            className={cn(
              'h-9 rounded-sm px-3 text-[13px] leading-[18px] font-medium',
              device === option
                ? 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-100'
                : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            )}
          >
            {t(`${base}.device_${option}`)}
          </button>
        ))}
      </div>

      {device === 'desktop' ? (
        <div className="relative h-[341px] w-full max-w-[480px] overflow-hidden rounded-4xl border border-gray-200 bg-white dark:border-gray-700">
          <div className="flex h-10 items-center bg-[#44403c] px-[19px]">
            <span className="flex shrink-0 gap-[3px]">
              <span className="size-2 rounded-full bg-[#d6d3d1]" />
              <span className="size-2 rounded-full bg-[#d6d3d1]" />
              <span className="size-2 rounded-full bg-[#d6d3d1]" />
            </span>
            <span className="ml-[14px] flex h-8 min-w-0 max-w-[348px] flex-1 items-center rounded-full border border-[#1c1917] bg-[#292524] px-[9px] text-[10px] leading-[18px] text-[#a8a29e]">
              {chromeLabel}
            </span>
          </div>
          {websiteStage ?? (
            <div className="relative h-[301px] bg-white">
              <div className="pointer-events-none absolute inset-0 z-1">{stage}</div>
            </div>
          )}
        </div>
      ) : (
        <div className="relative h-[600px] w-[277px] self-center overflow-hidden rounded-4xl bg-white shadow-[inset_0_0_0_4px_#e9eaeb]">
          {websiteStage ?? <div className="pointer-events-none absolute inset-0 z-1">{stage}</div>}
          <span className="absolute top-3 left-2 z-1 flex h-8 w-[261px] items-center justify-center rounded-full border border-[#1c1917] bg-[#292524] px-2 text-[10px] leading-[18px] text-[#a8a29e]">
            {chromeLabel}
          </span>
        </div>
      )}

      {(isMultiVenue || style.iconOnly) && (
        <p className="mt-4 max-w-[480px] text-[13px] leading-[18px] text-gray-600 dark:text-gray-400">
          {isMultiVenue ? t(`${base}.note_multi_venue`) : t(`${base}.note_icon_only`)}
        </p>
      )}
    </div>
  )
}
